import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { AuthShell } from './SignupPage';

export default function ResetPasswordPage() {
  const navigate = useNavigate();

  const [password,       setPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass,       setShowPass]       = useState(false);
  const [showConfirm,    setShowConfirm]    = useState(false);
  const [submitting,     setSubmitting]     = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [success,        setSuccess]        = useState(false);
  const [hasSession,     setHasSession]     = useState<boolean | null>(null);

  // On mount, check if Supabase has established a recovery session from the URL hash
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data.session);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Still checking for a session
  if (hasSession === null) {
    return (
      <AuthShell>
        <div className="flex justify-center py-8">
          <Loader2 size={28} className="animate-spin text-sage" />
        </div>
      </AuthShell>
    );
  }

  // No valid recovery session — link has expired or user arrived directly
  if (!hasSession) {
    return (
      <AuthShell>
        <div className="text-center">
          <div className="text-4xl mb-5">🔒</div>
          <h1 className="font-heading text-2xl text-navy mb-3">Link expired</h1>
          <p className="font-body text-sm text-gray-500 leading-relaxed mb-6">
            This password reset link has expired or is no longer valid.
            Request a new one from the login page.
          </p>
          <Link to="/login" className="btn-primary inline-block px-6 py-2 text-sm">
            Back to login
          </Link>
        </div>
      </AuthShell>
    );
  }

  // Success state
  if (success) {
    return (
      <AuthShell>
        <div className="text-center">
          <div className="text-4xl mb-5">✅</div>
          <h1 className="font-heading text-2xl text-navy mb-3">Password updated!</h1>
          <p className="font-body text-sm text-gray-500">
            Redirecting you to the login page…
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="text-center mb-7">
        <h1 className="font-heading text-3xl text-navy mb-2">Set a new password</h1>
        <p className="font-body text-sm text-gray-500">
          Choose a strong password, at least 8 characters.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">

        {/* New password */}
        <div className="relative">
          <label htmlFor="new-password" className="sr-only">New password</label>
          <input
            id="new-password"
            type={showPass ? 'text' : 'password'}
            placeholder="New password (8+ characters)"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={submitting}
            className="input pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPass((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy transition-colors"
            aria-label={showPass ? 'Hide password' : 'Show password'}
          >
            {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {/* Confirm password */}
        <div className="relative">
          <label htmlFor="confirm-password" className="sr-only">Confirm password</label>
          <input
            id="confirm-password"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Confirm new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            disabled={submitting}
            className="input pr-10"
          />
          <button
            type="button"
            onClick={() => setShowConfirm((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy transition-colors"
            aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
          >
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
            <p className="font-body text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full btn-primary flex items-center justify-center gap-2
                     disabled:opacity-50 disabled:cursor-not-allowed
                     disabled:hover:shadow-none disabled:hover:translate-y-0"
        >
          {submitting ? (
            <><Loader2 size={16} className="animate-spin" /><span>Updating…</span></>
          ) : (
            <span>Update password</span>
          )}
        </button>

      </form>

      <p className="mt-5 text-center font-body text-sm text-gray-500">
        Remembered your password?{' '}
        <Link to="/login" className="text-sage font-medium hover:underline">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
